<template>
  <v-container class="mb-4" fluid>
    <transition name="scroll-y-transition">
      <v-row v-if="hasTabelaPrecoItem">
        <v-col cols="12">
          <v-alert
            type="success"
            outlined
            v-once
            class="rounded-tag-small body-2"
            dismissible>
            Carregamos os preços dos produtos de acordo com suas tabelas de preço.
          </v-alert>
        </v-col>
      </v-row>
    </transition>
    <template v-for="(produto, index) in compra.produtos">
      <px-cadastro-compra-produtos-table-item :produto="produto"
                                              :compra="compra"
                                              :is-editing="isEditing"
                                              :is-a-vista="isAVista"
                                              :erp-campos-integracao="erpCamposIntegracao"
                                              :campos-obrigatorios="camposObrigatorios"
                                              :key="produto.$$id"
                                              ref="produtos"
                                              @remove="onRemoveProduto(index)"/>
    </template>
    <v-row dense class="mt-3">
      <v-col cols="12" class="text-right">
        <v-btn small
               color="primary"
               class="normal-btn-text elevation-0 rounded-tag-small"
               @click="onAddNewProduto">
          <v-icon small>mdi-plus</v-icon>
          Produto
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="4">
        <v-row dense no-gutters class="body-1">
          <v-col cols="6">
            Produtos:
          </v-col>
          <v-col cols="6" class="text-right">
            {{
              totalProdutos |
                currency(simboloMoeda, casaDecimal, configuracaoFormatacaoMoeda)
            }}
          </v-col>
          <v-col cols="6">
            Frete:
          </v-col>
          <v-col cols="6" class="text-right">
            {{
              totalTransporte |
                currency(simboloMoeda, 2, configuracaoFormatacaoMoeda)
            }}
          </v-col>
          <v-col cols="6" class="font-weight-bold">
            Total:
          </v-col>
          <v-col cols="6" class="text-right">
                  <span class="text--primary--color font-weight-bold">
                    {{
                      totalCompra |
                        currency(simboloMoeda, casaDecimal, configuracaoFormatacaoMoeda)
                    }}
                  </span>
          </v-col>
          <template v-if="savingCompra">
            <v-col cols="6" class="font-weight-bold">
              Saving:
            </v-col>
            <v-col cols="6" class="text-right">
                      <span class="text--success--color font-weight-bold">
                        {{
                          savingCompra |
                            currency(simboloMoeda, 2, configuracaoFormatacaoMoeda)
                        }}
                      </span>
            </v-col>
          </template>
        </v-row>
      </v-col>
    </v-row>
    <div class="mt-5">
      <v-btn color="primary"
             class="rounded-tag-small elevation-0 normal-btn-text"
             @click="salvar"
             :loading="loading"
             :disabled="!compra.produtos.length">
        Salvar
      </v-btn>
      <v-btn @click="saveDraft"
             v-if="!isEditing"
             class="rounded-tag-small elevation-0 normal-btn-text ml-2">
        Salvar rascunho
      </v-btn>
      <v-btn text class="normal-btn-text" @click="previous">Voltar</v-btn>
    </div>
  </v-container>
</template>

<script>
import validationMixin from '@/mixins/validation-mixin';
import {
  calcularTotaisCompra,
  calcularSaving,
} from '@/modules/compra/cadastro/calculo-compra.service';
import CadastroCompraProdutosTableItem
  from '@/modules/compra/cadastro/CadastroCompraProdutosTableItem.vue';
import { initalStateProduto, mapValoresTabelaPreco } from '@/modules/compra/cadastro/compra-utils';
import TabelaPrecoService from '@/modules/tabela-preco/tabela-preco-service';
import { formatToCode } from '@/utils/moment-date-utils';
import moment from 'moment';
import { FormaPagamento } from 'px-business-components';
import moedaMixin from '@/mixins/moeda-mixin';
import { CasaDecimalUtils } from 'px-components';

export default {
  mixins: [validationMixin, moedaMixin],
  props: {
    compra: {
      type: Object,
      required: true,
    },
    erpCamposIntegracao: String,
    camposObrigatorios: Object,
    loading: Boolean,
    isEditing: Boolean,
    isRascunho: Boolean,
    usaTabelaPreco: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      itensTabelaPreco: [],
    };
  },
  computed: {
    moeda() {
      return this.compra.moeda;
    },
    totalCompra() {
      return calcularTotaisCompra(this.compra).total || 0;
    },
    totalProdutos() {
      return calcularTotaisCompra(this.compra).totalProdutos || 0;
    },
    savingCompra() {
      return calcularSaving(this.compra);
    },
    totalTransporte() {
      return this.compra.frete.totalTransporte || 0;
    },
    isAVista() {
      return this.compra.formaPagamento === FormaPagamento.keys.A_VISTA.key;
    },
    hasTabelaPrecoItem() {
      return this.compra.produtos.find(p => p.$$itemTabelaPreco);
    },
    casaDecimal() {
      const mappedProdutos = this.compra.produtos
        .map(produto => produto.produto)
        .filter(produto => produto);
      return CasaDecimalUtils.getCasaDecimal(mappedProdutos);
    },
  },
  methods: {
    validate() {
      return this.$refs.produtos.map(produto => produto.validate())
        .every(validation => validation);
    },
    onAddNewProduto() {
      this.compra.produtos.push(initalStateProduto());
    },
    onRemoveProduto(index) {
      this.compra.produtos.splice(index, 1);
    },
    salvar() {
      if (!this.validate()) {
        return;
      }
      this.$emit('ready');
    },
    previous() {
      this.$emit('previous');
    },
    saveDraft() {
      if (!this.validate()) {
        return;
      }
      this.$emit('save-draft');
    },
    /**
     * Faz o update dos valores que dependem do prazo e forma de pagamento
     */
    updateValoresTabelaPreco() {
      this.compra.produtos = this.compra.produtos.map((produto) => {
        if (!produto.produto || !produto.$$itemTabelaPreco) {
          return produto;
        }
        return mapValoresTabelaPreco(produto, produto.$$itemTabelaPreco, this.isAVista);
      });
    },
    setValoresTabelaPreco() {
      if (!this.itensTabelaPreco || !this.itensTabelaPreco.length
          || !this.compra.produtos.length) {
        return;
      }
      this.compra.produtos = this.compra.produtos.map((produto) => {
        if (!produto.produto) {
          return produto;
        }
        const itemTabelaPreco = this.itensTabelaPreco
          .find(item => item.produto.id === produto.produto.id);
        if (!itemTabelaPreco) {
          return produto;
        }
        return mapValoresTabelaPreco(produto, itemTabelaPreco, this.isAVista);
      });
    },
    getTabelaPreco() {
      if (!this.compra.produtos.length) {
        return;
      }
      const produtosFilters = this.compra.produtos
        .filter(p => p.produto)
        .map(p => `produto.id = ${p.produto.id}`);
      if (!produtosFilters || !produtosFilters.length) {
        return;
      }
      const dataAtual = formatToCode(moment());
      const query = `fornecedor.id = ${this.compra.fornecedor.id} and ativo = true and (${produtosFilters.join(' or ')}) and dataVigenciaInicial <= ${dataAtual} and dataVigenciaFinal >= ${dataAtual}`;
      TabelaPrecoService.getItensTabelaPreco({
        query,
      })
        .then(({ data }) => {
          if (!data || !data.length) {
            return;
          }
          this.itensTabelaPreco = data;
          this.setValoresTabelaPreco(data);
        });
    },
    computaTabelaPrecoFornecedor() {
      if (!this.usaTabelaPreco || this.editing) {
        return;
      }
      this.getTabelaPreco();
    },
  },
  watch: {
    'compra.fornecedor': function (newValue, oldValue) {
      if (newValue === oldValue || !newValue) {
        return;
      }
      this.computaTabelaPrecoFornecedor();
    },
    'compra.formaPagamento': function (newValue, oldValue) {
      if (newValue === oldValue || !newValue) {
        return;
      }
      this.updateValoresTabelaPreco();
    },
  },
  components: {
    pxCadastroCompraProdutosTableItem: CadastroCompraProdutosTableItem,
  },
};
</script>
